import * as React from "react";
import { Fragment } from "react";
import {
  DatagridConfigurable,
  ExportButton,
  SelectColumnsButton,
  TextField,
  TextInput,
  TopToolbar,
  WrapperField,
  FunctionField,
  downloadCSV,
} from "react-admin";
import { useMediaQuery, Divider, Theme, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IfCanAccess } from "@react-admin/ra-rbac";
import ComponentList from "../../../components/PageComponents/ComponentList";
import { ListButtons, FilterForm } from "../../../components/PageComponents/ComponentList";
import { ProjectFilter } from "../ProjectFilter";
import { ProjectField } from "../ProjectField";
import { Helmet } from "react-helmet-async";
import { StatementStatusLabel } from "./StatementStatusLabel";
import MobileGrid from "./MobileGrid";
import GenerateAction from "./GenerateAction";
import { datagridSx } from "../../../components/PageComponents/datagridSx";
import { formatEuro } from "../../../utils/paymentFormat";

const statementFilters = [
  <ProjectFilter source="projectId" />,
  <TextInput source="month" label="Month" placeholder="2026-06" helperText={false} />,
];

const customExporter = (data, fetchRelatedRecords, dataProvider) => {
  return fetchRelatedRecords(data, "projectId", "projects").then((projects) => {
    const exportData = data.map((record) => ({
      month: record.month,
      project: projects[record.projectId]?.name || record.projectId,
      totalFeeEur: record.totalFeeEur,
      status: record.status,
      validatedBy: record.validatedBy,
      validatedAt: record.validatedAt,
    }));

    const headers = Object.keys(exportData[0] || {}).join(",");
    const rows = exportData.map((item) =>
      Object.values(item)
        .map((value) =>
          typeof value === "string" ? `"${value.replace(/"/g, '""')}"` : value
        )
        .join(",")
    );

    downloadCSV([headers, ...rows].join("\n"), "gas-station-statements");
  });
};

const ListActions = ({ filters }: { filters: any[] }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 2,
      width: "100%",
      "& > .MuiToolbar-root": { flexShrink: 0 },
    }}
  >
    <FilterForm filters={filters} />
    <TopToolbar>
      <GenerateAction />
      <SelectColumnsButton />
      <IfCanAccess action="export" resource="gasStationStatements">
        <ExportButton exporter={customExporter} />
      </IfCanAccess>
    </TopToolbar>
  </Box>
);

const GasStationStatementList = () => {
  const { t }: { t: any } = useTranslation();
  return (
    <>
      <Helmet>
        <title>Coinhouse Solutions - Gas station statements</title>
      </Helmet>
      <ComponentList
        title={t("Gas station statements")}
        subtitle={t("Monthly gas fees billed to each entity, to validate and send.")}
        actions={<ListActions filters={statementFilters} />}
        sort={{ field: "month", order: "DESC" }}
      >
        <TabbedDatagrid />
      </ComponentList>
    </>
  );
};

const TabbedDatagrid = () => {
  const isXSmall = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("sm")
  );
  return (
    <Fragment>
      <Divider />
      {isXSmall ? (
        <MobileGrid />
      ) : (
        <DatagridConfigurable
          rowClick="show"
          sx={datagridSx}
        >
          <TextField sortable={false} source="month" label="Month" />
          <ProjectField label="Entity" sortable={false} />
          <FunctionField sortable={false} label="Total fee" textAlign="right" render={(record) => (record.totalFeeEur !== undefined ? formatEuro(record.totalFeeEur) : "—")} />
          <WrapperField sortable={false} label="Status">
            <StatementStatusLabel />
          </WrapperField>
          <ListButtons ressource="gasStationStatements" />
        </DatagridConfigurable>
      )}
    </Fragment>
  );
};

export default GasStationStatementList;
